// client/src/components/contacts/ContactAISummary.tsx
// AI-powered contact communication summary component

import { Sparkles, MessageSquare, Clock, AlertCircle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import {
  useGetContactCommunicationSummaryQuery,
  useGetAIMessagingStatusQuery,
} from '../../api/aiMessagingApi';

interface ContactAISummaryProps {
  contactId: string;
}

export function ContactAISummary({ contactId }: ContactAISummaryProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Check if AI messaging is available
  const { data: statusData } = useGetAIMessagingStatusQuery();

  // Fetch AI communication summary
  const {
    data: summaryData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetContactCommunicationSummaryQuery(contactId, {
    skip: !statusData?.available || !contactId,
  });

  // Don't render if AI messaging is not available
  if (!statusData?.available) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-purple-600">
          <Sparkles className="w-4 h-4 animate-pulse" />
          <span className="text-sm">Generating insights...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Failed to load communication insights</span>
          </div>
          <button
            onClick={() => refetch()}
            className="text-xs text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!summaryData) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg mb-4 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-purple-50/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-purple-900">What's Going On</span>
          {summaryData.unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded-full">
              {summaryData.unreadCount} unread
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              refetch();
            }}
            className={`p-1 rounded hover:bg-purple-100 transition-colors ${isFetching ? 'animate-spin' : ''}`}
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5 text-purple-500" />
          </button>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-purple-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-purple-500" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Summary */}
          <div className="bg-white/60 rounded-lg p-3">
            <p className="text-sm text-gray-700">{summaryData.summary}</p>
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5 text-gray-600">
              <MessageSquare className="w-3.5 h-3.5 text-purple-500" />
              <span>{summaryData.threadCount} conversation{summaryData.threadCount !== 1 ? 's' : ''}</span>
            </div>
            {summaryData.lastContactedAt && (
              <div className="flex items-center gap-1.5 text-gray-600">
                <Clock className="w-3.5 h-3.5 text-purple-500" />
                <span>Last contact: {formatRelativeDate(summaryData.lastContactedAt)}</span>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          {summaryData.recentActivity && (
            <div className="bg-white/40 rounded-lg p-2">
              <p className="text-xs text-gray-600">
                <span className="font-medium text-purple-700">Recent: </span>
                {summaryData.recentActivity}
              </p>
            </div>
          )}

          {/* Open Items */}
          {summaryData.openItems.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-purple-700">Open Items:</p>
              <ul className="space-y-1">
                {summaryData.openItems.map((item, idx) => (
                  <li key={idx} className="text-xs text-gray-600 flex items-start gap-1.5">
                    <span className="text-purple-400 mt-0.5">-</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}

export default ContactAISummary;
